import { Resume, User } from '@sr/types';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useUser } from '../../hooks/use-user';
import {
  ArrowRightIcon,
  Box,
  Button,
  CrossIcon,
  DownloadIcon,
  Image,
  MoreIcon,
  ContentWithSidebar,
  Text,
  TUILink,
  LockIcon,
} from '../../ui';
import API from '../../utils/api';
import { downloadResume } from '../../utils/download-resume';
import isPro from '../../utils/is-pro';
import HelpButton from '../help/HelpButton';
import ChangeSlug from './ChangeSlug';
import CopyResumeUrl from './CopyResumeUrl';
import HideBranding from './HideBranding';
import HidePhoneAndEmail from './HidePhoneAndEmail';
import ProBadge from './ProBadge';
import VisibilityPicker from './VisibilityPicker';
import { useIsMobileOrTablet } from 'v1/hooks/use-is-media-query';

type Props = {
  resume: Resume;
  isVisible: boolean;
};

const Share = ({ resume, isVisible }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const user: User | null | false = useUser();

  if (!isVisible || !user) return null;

  return (
    <ContentWithSidebar
      openButtonContents={<OpenButtonContents />}
      closeButtonContents={<CrossIcon color="white" />}
      sidebarContent={
        <Sidebar
          resume={resume}
          isPro={isPro(user)}
          openProModal={() => setModalOpen(true)}
        />
      }
      mainContent={
        <Content
          isPro={isPro(user)}
          modalOpen={modalOpen}
          resume={resume}
          user={user}
        />
      }
      rightSidebarContent={<RightSidebar />}
    />
  );
};

function Content({
  isPro,
  modalOpen,
  resume,
  user,
}: {
  isPro: boolean;
  modalOpen: boolean;
  resume: Resume;
  user: User;
}) {
  const history = useHistory();
  const [isDownloading, setIsDownloading] = useState(false);
  const isPrivate = resume.visibility === 'private';
  const isMobileOrTablet = useIsMobileOrTablet();

  function handleClick(e: React.SyntheticEvent<HTMLButtonElement>) {
    if (!isPro) {
      e.preventDefault();
      if (user.isAnonymous) {
        document.location.href = '/v2/setup/account?fromapp=true';
      } else {
        history.push('/upgrade?refLink=shareDownload');
      }
    } else {
      setIsDownloading(true);
      downloadResume(resume.id).then(() => {
        setIsDownloading(false);
      });
    }
    API.events.logEvent({
      userId: user.id,
      eventName: 'Resume PDF Downloaded',
      properties: {
        Blocked: isPro ? 'False' : 'True',
        Location: 'Share',
      },
    });
  }

  return (
    <Box
      pt={6}
      sx={{ display: 'flex', flexDirection: ['column', 'row', 'row'] }}
    >
      <Button
        onClick={handleClick}
        data-testid="download-pdf"
        sx={{
          position: 'relative',
          display: 'block',
          textDecoration: 'none',
          mr: [0, 4],
          color: 'blue',
          mb: 6,
          opacity: isDownloading ? 0.7 : 1,
          transition: 'opacity 300ms linear',
          // Undo styles set by <Button>
          bg: 'transparent',
          padding: 0,
          fontWeight: 'body',
          width: 'auto',
        }}
      >
        <Image src="https://assets.standardresume.co/f_auto,q_auto/v1/app/share/pdf_jecpwz.png" />
        <Box
          sx={{
            variant: 'flex.center',
            fontSize: 'title',
            py: 3,
            transition: 'all 200ms linear',
            ':hover': {
              filter: 'brightness(140%)',
            },
          }}
        >
          {!isPro ? (
            <>
              <LockIcon color="blue" mr={2} />
              Upgrade to download PDF
            </>
          ) : (
            <>
              <DownloadIcon color="blue" mr={2} />
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </>
          )}
        </Box>
        {!isPro && (
          <Box
            sx={{
              position: 'absolute',
              top: 13,
              right: 21,
            }}
          >
            <ProBadge size="large" />
          </Box>
        )}
      </Button>
      <TUILink
        href={isPrivate ? '' : getPublicResumeUrl(resume)}
        target="_blank"
        data-testid="open-web-resume"
        onClick={(e) => {
          if (isPrivate) e.preventDefault();
          API.events.logEvent({
            userId: resume.owner,
            eventName: 'Resume Website Viewed ',
          });
        }}
        sx={{
          position: 'relative',
          display: 'block',
          textDecoration: 'none',
          color: 'blue',
          cursor: isPrivate ? 'not-allowed' : 'pointer',
        }}
        id="gi-share-web-resume-link"
      >
        {isPrivate && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 94,
              left: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Box>
              <Text variant="baseEmphasis" mb={2}>
                Your resume is set to private
              </Text>
              {isMobileOrTablet ? (
                <Text color="black50">
                  To view it, change the permissions to public
                  <br /> by tapping the settings button below
                </Text>
              ) : (
                <Text color="black50">
                  To view it, change the permissions to public
                  <br /> using the permissions panel on the left
                </Text>
              )}
            </Box>
          </Box>
        )}
        <Image
          src="https://assets.standardresume.co/f_auto,q_auto/v1/app/share/web_jdhi5d.png"
          sx={{
            opacity: isPrivate ? 0.3 : 1,
          }}
        />
        <Box
          sx={{
            variant: 'flex.center',
            fontSize: 'title',
            py: 3,
            opacity: isPrivate ? 0.3 : 1,
          }}
        >
          View web resume
          <ArrowRightIcon ml={2} color="blue" />
        </Box>
      </TUILink>
      <HelpButton userId={resume.owner} />
    </Box>
  );
}

function Sidebar({
  isPro,
  openProModal,
  resume,
}: {
  isPro: boolean;
  openProModal: () => void;
  resume: Resume;
}) {
  return (
    <Box>
      <VisibilityPicker resume={resume} />
      <Text variant="listHeading" pb="4px">
        Web Resume Options
      </Text>
      <HideBranding resume={resume} isPro={isPro} />
      <HidePhoneAndEmail resume={resume} />
      <ChangeSlug resume={resume} />
      <CopyResumeUrl resume={resume} />
    </Box>
  );
}

function OpenButtonContents() {
  return (
    <>
      <MoreIcon size={24} pr={2} color="white" /> Settings
    </>
  );
}

export const getPublicResumeUrl = (resume: Resume): string => {
  return `${process.env.PUBLIC_WEBSITE_URL || ''}/r/${resume.slug}`;
};

function RightSidebar() {
  const blueText = {
    backgroundColor: 'blue',
    color: 'white',
    lineHeight: 1,
    fontWeight: 'bold',
    fontSize: 28,
    px: 1,
  };
  const linkStyle = {
    color: 'blue',
    // fontWeight: 'bold',
    textDecoration: 'none',
    marginTop: 2,
    ':hover': { textDecoration: 'underline' },
  };
  return (
    <div>
      <Box
        sx={{
          marginTop: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          bg: 'white',
          padding: '14px 12px 16px 14px',
        }}
      >
        <Box sx={blueText}>The</Box>
        <Box sx={blueText}>Resume</Box>
        <Box sx={blueText}>Handbook</Box>
        <Box
          sx={{ fontSize: 14, lineHeight: 1.5, mt: 3, mb: 2, color: 'black70' }}
        >
          Wisdom from 8 years of helping over 200,000 people create a winning
          resume, combined with insights from top recruiters and hiring
          managers.
        </Box>
        <TUILink
          href="https://learn.standardresume.co/design/file-format"
          target="_blank"
          sx={linkStyle}
        >
          Why PDF is Better than Word →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co/optimize/external-feedback"
          target="_blank"
          sx={linkStyle}
        >
          Using External Feedback →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co/optimize/understand-the-ats"
          target="_blank"
          sx={linkStyle}
        >
          Understand the ATS →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co"
          target="_blank"
          sx={linkStyle}
        >
          Overview →
        </TUILink>
      </Box>
    </div>
  );
}

export default Share;
